// Header.tsx (FULLY FINAL & RESPONSIVE)
import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, Menu, X } from 'lucide-react'; 
import { NavLink } from 'react-router-dom';
import logoImage from '../assets/vermilogo (2).png'; 
import logo from '../assets/vermilogo.png';

// Define the type for the navigation items
interface NavItem {
  name: string;
  href: string;
}

const navItems: NavItem[] = [
  { name: 'Home', href: '/' },
  { name: 'About Us', href: 'about' },
  { name: 'Science & Studies', href: '/study' },
  { name: 'Future Plans', href: '/plan' },
  { name: 'Gallery', href: '/gallery' },
  { name: 'Contact Us', href: '/contact' },
];

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false); 
  const [mobileTopBarHeight, setMobileTopBarHeight] = useState(90);
  const topBarRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => setIsOpen(!isOpen);

  // Dynamically calculate the height of the top bar for precise mobile menu positioning
  useEffect(() => {
    if (topBarRef.current) {
      // Get the actual height of the top bar div
      setMobileTopBarHeight(topBarRef.current.offsetHeight);
    }
    
    // Global Fix: Ensure overflow-x is hidden when the mobile menu is open
    // This prevents the side-space issue caused by the menu sliding in.
    if (isOpen) {
        document.body.style.overflowX = 'hidden';
    } else {
        document.body.style.overflowX = ''; // Revert to default when closed
    }
    
    // Clean up the style
    return () => {
        document.body.style.overflowX = '';
    };

  }, [isOpen]);

  
  return (
    // FIX: Removed 'overflow-x-hidden' from header as it can interfere with fixed positioning, 
    // relying on the body overflow fix instead.
    <header className="w-full fixed top-0 z-50"> 
      
      {/* 1. Top Bar (White Background) */}
      <div ref={topBarRef} className={`py-2 bg-white border-b border-gray-200 `}>
         
        {/* ======================================================= */}
        {/* DESKTOP LAYOUT (md:flex) */}
        {/* ======================================================= */}
        <div className="hidden md:flex items-center justify-between px-6 py-4 h-16">
           
          {/* LOGO AREA (Desktop) */}
          <div className="flex items-center space-x-2 ">
            <a href="/" className=' flex items-center'> 
             <img 
              src={logo} 
              alt="Vermi Compost Logo Icon" 
              className="h-16 w-16 object-contain "
            />
    
             <img 
              src={logoImage} 
              alt="Vermi Compost Logo Text" 
              className="h-32 object-contain " 
            />
            </a>
          </div>
          
          {/* CONTACT DETAILS (Desktop) */}
          <div className="flex items-center">
              <div className="grid grid-cols-2 divide-x divide-gray-300 border-x border-gray-300 px-8"> 
                  {/* Phone/WhatsApp Column */}
                  <div className="flex flex-col items-center justify-center px-4">
                    {/* <span className="text-gray-900 font-semibold mb-1 text-sm">Phone/WhatsApp</span> */}
                    <div className="flex items-center space-x-1">
                      <Phone size={20} className="text-green-700" />
                      <span className="text-gray-700 font-medium text-sm">+91 6395178971</span>
                    </div>
                  </div>

                  {/* Email Column */}
                  <div className="flex flex-col items-center justify-center px-4">
                    {/* <span className="text-gray-900 font-semibold mb-1 text-sm">Email</span> */}
                    <div className="flex items-center space-x-1">
                      <Mail size={20} className="text-green-700" />
                      <span className="text-gray-700 font-medium text-1xl">info@bareillyvermicompost.com</span>
                    </div>
                  </div>
              </div>
          </div>
        </div>

        {/* ======================================================= */}
        {/* MOBILE LAYOUT (md:hidden) */}
        {/* ======================================================= */}
        <div id="mobile-top-bar" className="md:hidden flex items-center justify-between px-3 py-2 min-h-[80px]">
            
            {/* 1. LOGO (Mobile Left) */}
           <div className="flex items-center flex-shrink-0">
  <a href="/" className="flex items-center gap-2">
    <img 
      src={logo} 
      alt="Vermi Compost Logo Icon" 
      className="h-16 w-16 object-contain"
    />
    <img 
      src={logoImage} 
      alt="Vermi Compost Logo" 
      className="h-24 w-auto object-contain"
    />
  </a>
</div>


            {/* 2. CONTACT DETAILS (Mobile Center) */}
            {/* <div className="flex flex-col items-end text-[15px] space-y-0.5 mx-1 overflow-hidden">
                
                <a href="tel:+916395178971" className="flex items-center space-x-1 text-gray-700 whitespace-nowrap">
                    <Phone size={12} className="text-green-700 flex-shrink-0" />
                    <span className="truncate">+91 6395178971</span>
                </a>
               
                <a href="mailto:info@bareillyvermicompost.com" className="flex items-center space-x-1 text-gray-700 whitespace-nowrap">
                    <Mail size={12} className="text-green-700 flex-shrink-0" />
                    <span className="truncate">info@bareillyvermicompost.com</span>
                </a>
            </div> */}

            {/* 3. HAMBURGER BUTTON (Mobile Right) - FIX: Removed -translate-x-2 */}
            <button 
                onClick={toggleMenu} 
                className="text-green-800 p-2 focus:outline-none flex-shrink-0"
            >
                {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>

        </div>


      </div>

      {/* 2. Main Navigation Bar (Dark Green Background) - DESKTOP ONLY */}
      <nav className="bg-[#4f6a39] shadow-lg hidden md:block">
        <ul className="flex justify-center text-white text-sm font-medium">
          {navItems.map((item) => (
            <li key={item.name}>
              <NavLink
                to={item.href}
                className={({ isActive }) =>
                  `block px-4 py-3 transition duration-200 ease-in-out ${
                    isActive
                      ? "bg-white text-green-800"
                      : "hover:bg-white hover:text-green-800"
                  }`
                }
              >
                {item.name}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* MOBILE SIDE MENU */}
      <nav 
        className={`fixed right-0 h-full w-64 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        // Use the dynamically calculated height
        style={{ top: `${mobileTopBarHeight}px`, maxHeight: `calc(100vh - ${mobileTopBarHeight}px)` }} 
      >
        
        <ul className="flex flex-col p-4 text-gray-800">
          {navItems.map((item) => (
            <li key={item.name} className="border-b border-gray-100">
              <NavLink
                to={item.href}
                onClick={toggleMenu}
                className={({ isActive }) =>
                  `block px-4 py-3 text-base font-medium transition duration-200 ${
                    isActive
                      ? "bg-green-100 text-green-800 border-l-4 border-green-700"
                      : "hover:bg-gray-50"
                  }`
                }
              >
                {item.name}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* OVERLAY */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black opacity-50 md:hidden z-40" 
          onClick={toggleMenu}
        />
      )}
    </header>
  );
};

export default Header;